import React, { useState, useEffect } from "react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { getClient, putClient } from "../API/clientAPI";
import { getJobOrder, putJobOrder, patchJobOrderLink } from "../API/jobOrderAPI";
import { getTechnicians } from "../API/technicianAPI";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";
import { sendEmailQueue } from "../API/emailAPI";

import AreasTable from "./FormComponents/AreasTable";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import PageOneReviewSection from "./FormComponents/PageOneReviewSection";
import SectionTimestampNotice from "./FormComponents/SectionTimestampNotice";
import QcServiceDetailsSection from "./FormComponents/QcServiceDetailsSection";
import QcFindingsRecommendationsSection from "./FormComponents/QcFindingsRecommendationsSection";
import QcCustomerResponsibilitiesSection from "./FormComponents/QcCustomerResponsibilitiesSection";
import QcChemicalControlSection from "./FormComponents/QcChemicalControlSection";
import TechnicianOnlyReviewSection from "./FormComponents/TechnicianOnlyReviewSection";
import ClientOnlyReviewSection from "./FormComponents/ClientOnlyReviewSection";
import DocumentLinksCard from "./FormComponents/DocumentLinksCard";
import { buildGeneratedDocumentLinks } from "./FormComponents/email/buildGeneratedDocumentLinks";
import { buildServiceReportEmail } from "./FormComponents/email/buildServiceReportEmail";
import { getEffectiveEndTime } from "./utils/timeFormatter";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function EditQcForm() {
  const { id } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('returnTo');
  
  const [jobOrder, setJobOrder] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [sendEmailAfterEdit, setSendEmailAfterEdit] = useState(true);

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  const [jobOrderParameters, setjobOrderParameters] = useState({
    status: "",
    chemical_used: [],
    job_date: "",
    start_time: "",
    end_time: "",
    accomplished_by_technician: "",
    reminder_notes: "",
    satisfaction_rating: 0,
  });

  const [client, setClient] = useState({});
  const [editedClient, setEditedClient] = useState();
  const [links, setLinks] = useState({});

  // all techs for the dialog
  const [allTechnicians, setAllTechnicians] = useState([]);

  const [technicianSignatory, setTechnicianSignatory] = useState("");
  const [satisfactionRating, setSatisfactionRating] = useState(0);
  const [specialNotes, setSpecialNotes] = useState("");
  const [appointmentType, setAppointmentType] = useState("");
  const [treatmentType, setTreatmentType] = useState("");

  const [controlAgainst, setControlAgainst] = useState({
    crawlingInsects: false,
    flyingInsects:   false,
    rodentControl:   false,
    others:          false,
  });
  const [otherControlText, setOtherControlText] = useState("");

  // Signature-related state
  const [accomplishedSignatureURL, setAccomplishedSignatureURL] = useState("");
  const [acknowledgedSignatureURL, setAcknowledgedSignatureURL] = useState("");
  const [acknowledgedClientSignatory, setAcknowledgedClientSignatory] = useState("");

  const navigate = useNavigate();

  // RHF — single form instance for the page
  const {
    control,
    register,
    setValue,
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      findingsAreas: [
        {
          location: "",
          pests: { Roaches: 0, Mosq: 0, Rodents: 0, Flies: 0, Ants: 0, Others: 0 },
        },
      ],
      recommendations: [
      ],
    },
  });

  // ---- FINDINGS AREAS ----
  const {
    fields: findingsAreas,
    append: addFinding,
    remove: removeFinding,
    replace: replaceFindingAreas,
  } = useFieldArray({
    control,
    name: "findingsAreas",
  });

  // ---- RECOMMENDATIONS ----
  const MAX_RECOMMENDATIONS = 8;
  
  const RECOMMENDATION_OPTIONS = [
    { value: "", label: "None" },
    { value: "Keep areas clean and dry", label: "Keep areas clean and dry" },
    { value: "Keep trash bins covered", label: "Keep trash bins covered" },
    { value: "Dispose Garbage Daily", label: "Dispose Garbage Daily" },
    { value: "Seal / Caulking of Cracks and Crevices", label: "Seal / Caulking of Cracks and Crevices" },
    { value: "Keep doors/windows closed", label: "Keep doors/windows closed" },
    { value: "Fix leaking pipes and gutters", label: "Fix leaking pipes and gutters" },
    { value: "Organize Piling and Storage", label: "Organize Piling and Storage" },
    { value: "Make room available for inspection", label: "Make room available for inspection" },
    { value: "Install screens/weather strip", label: "Install screens/weather strip" },
    // Added to support free-text:
    { value: "Other", label: "Other (specify)" },
  ];

  const {
    fields: recFields,
    append: appendRec,
    remove: removeRec,
  } = useFieldArray({
    control,
    name: "recommendations",
  });

  // Watch all recommendation rows to know when to show text fields
  const recValues = useWatch({ control, name: "recommendations" }) || [];

  // 1. derive a lookup from the raw arrays for label ↔ value mapping
  const formatKey = s => s.toLowerCase().replace(/\s+/g, "_");

  // 2. filter & sort selected job types
  const selectedJobTypes = (jobOrder?.job_types || [])
    // map back to the human-readable label
    .map(val => JOB_TYPES.find(opt => formatKey(opt) === val))
    // remove any non-matches
    .filter(Boolean)
    // alphabetical
    .sort((a, b) => a.localeCompare(b));

  // 3. same for descriptions
  const selectedJobDescriptions = (jobOrder?.job_description || [])
    .map(val => JOB_DESCRIPTIONS.find(opt => formatKey(opt) === val))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  // 4. for display purposes in non-editable sections
  const displayedJobTypes = selectedJobTypes;
  const displayedJobDescriptions = selectedJobDescriptions;

  const [chemical_used, setChemicalUsed] = useState([
    { label: "1", chemical: "" },
    { label: "2", chemical: "" },
    { label: "3", chemical: "" },
    { label: "4", chemical: "" },
    { label: "5", chemical: "" },
    { label: "6", chemical: "" },
  ]);

  const handleChemicalInputChange = (index, field, value) => {
    const newChemicalUsed = [...chemical_used];
    newChemicalUsed[index][field] = value;
    setChemicalUsed(newChemicalUsed);
  };

  const handleControlAgainstChange = (field) => (event) => {
    setControlAgainst(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  // Type of Structure (checkboxes) + side text fields
  const [structureType, setStructureType] = useState({
    residential: false,
    commercialEstablishment: false,
    others: false,
  });

  const [structureNatureOfBusiness, setStructureNatureOfBusiness] = useState("");
  const [structureOthersText, setStructureOthersText] = useState("");

  const handleStructureTypeChange = (field) => (event) => {
    const checked = event.target.checked;
    setStructureType(prev => ({ ...prev, [field]: checked }));
  };

  // Nature of Work (checkboxes) + side text field
  const [natureOfWork, setNatureOfWork] = useState({
    generalPestControl: false,
    disinfection: false,
    fumigation: false,
    rodentControl: false,
    foggingMisting: false,
    others: false,
  });
  const [natureOfWorkOthersText, setNatureOfWorkOthersText] = useState("");

  const handleNatureOfWorkChange = (field) => (event) => {
    const checked = event.target.checked;
    setNatureOfWork(prev => ({ ...prev, [field]: checked }));
  };

  // For General Pest Control (GPC) section
  const [gpcFlyingCrawling, setGpcFlyingCrawling] = useState(false);
  const [gpcInsectsDetail, setGpcInsectsDetail] = useState({
    cockroach: false,
    flies: false,
    mosquitoes: false,
  });
  const [gpcRodents, setGpcRodents] = useState(false);
  const [gpcOtherPests, setGpcOtherPests] = useState(false);
  const [gpcOtherPestsText, setGpcOtherPestsText] = useState("");

  // Action Taken / Treatment Done section
  const [actionTaken, setActionTaken] = useState({
    mistSpraying: false,
    thermalFogging: false,
    coldFogging: false,
    larviciding: false,
    gelBaiting: false,
    uvLight: false,
    wiping: false,
    steaming: false,
    others: false,
  });
  const [actionTakenOthersText, setActionTakenOthersText] = useState("");

  const handleActionTakenChange = (field) => (event) => {
    const checked = event.target.checked;
    setActionTaken(prev => ({ ...prev, [field]: checked }));
  };

  // Customer's Responsibilities
  const [customerResp, setCustomerResp] = useState({
    sanitation1: "",
    sanitation2: "",
    sanitation3: "",
    sealing: "",
    barriers: "",
    repairsMaintenance: "",
    renovation: "",
  });

  // Findings - Degree of Infestation
  const [infestationFindings, setInfestationFindings] = useState({
    Roaches: "",
    Mosquitoes: "",
    Flies: "",
    Ants: "",
    Rodents: "",
  });

  const handleInfestationChange = (pest, level) => {
    setInfestationFindings(prev => ({ ...prev, [pest]: level }));
  };

  // --- AREAS TREATED: helpers & constants ---
  const makeIdxKey = (rIdx, cIdx) => `r${rIdx}-c${cIdx}`;

  const AREAS_TREATED_LAYOUT = [
    ["Office", "Tables", "Computer", "Cabinets", "Drawer"],
    ["Counter Area", "Cash register", "Telephone", "Counter table", null],
    [null, "Signage", "Softdrinks Dispensing machine", null, null],
    [null, "Soda fountain", "Checkout Stands", null, null],
    ["Dining Area", "Tables", "Chairs", "Walls", "Corners"],
    ["Kitchen", "Flooring", "Walling", "Corner", "Ceiling"],
    [null, "Sink", "Cabinets", "Tables", "Freezer"],
    [null, "Oven", "Fridge", "Chiller", "Kitch eq"],
    [null, "Meat counter", "Meat cutting blocks", null, "Pallets"],
    [null, "CR", "Drainage", null, null],
    ["Bedrooms", "Flooring", "Walling", "Bench", null],
    ["Dishwashing Area", "Sink", "Cabinet", "Table", null],
    ["Storage Area", "Freezer", "Coolers", null, null],
    ["Stock Area", "Cabinets", "Corners", null, null],
    ["Locker Room", "Lockers", "Wall", null, null],
    ["Crawl Space", "Stock items", "Fuse boxes", null, null],
    ["CR", "Sides/ Walls", "Switches", null, null],
    ["Receiving Area", "Sofa", "Canals", null, null],
    ["Hallway/ Stairway", "Corners", "Generator Rm", null, null],
    ["Electrical Circuits", "Switch boxes", "Guardhouse", null, null],
    [null, "Receptacle", "Refrigerators", null, null],
    [null, "Drainage", "Sinks", null, null],
    ["Surroundings", "Gas Room", "Electrical Rm", null, null],
    ["Other areas", "Drainage area", "Plants", null, null],
  ];

  // find the coordinates of a label (case-insensitive)
  const findLabelKey = (layout, search) => {
    const target = String(search).toLowerCase();
    for (let r = 0; r < layout.length; r++) {
      for (let c = 0; c < layout[r].length; c++) {
        const lbl = layout[r][c];
        if (lbl && String(lbl).toLowerCase() === target) {
          return makeIdxKey(r, c);
        }
      }
    }
    return null;
  };

  // Areas treated state (editable)
  const [areasTreatedState, setAreasTreatedState] = useState({});
  const [otherAreasText, setOtherAreasText] = useState("");

  const toggleAreaTreated = (key) => () => {
    setAreasTreatedState(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // toggle 'Other areas' text enablement only if "Other areas" cell is checked
  const OTHER_AREAS_KEY = React.useMemo(
    () => findLabelKey(AREAS_TREATED_LAYOUT, "Other areas"),
    []
  );

  const ratingBtnSx = (selected) => ({
    fontSize: '1.25rem',
    minWidth: '40px',
    height: '40px',
    p: 0,
    borderRadius: '50%',
    transition: 'all 0.2s ease-in-out',
    bgcolor: selected ? 'primary.main' : 'transparent',
    color: selected ? 'common.white' : 'text.secondary',
    border: selected ? 'none' : '1px solid',
    borderColor: selected ? 'transparent' : 'divider',
    transform: selected ? 'scale(1.2)' : 'scale(1)',
    '&:hover': {
      bgcolor: selected ? 'primary.dark' : 'grey.100',
    },
    cursor: 'pointer',
  });

  // Helper functions
  const handleJObOrderChange = (field, value) => {
    setjobOrderParameters((prev) => {
      return { ...prev, [field]: value };
    });
  };

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        
        // Fetch job order data
        const jobOrderData = await getJobOrder(id);
        setJobOrder(jobOrderData || {});
        setSatisfactionRating(jobOrderData?.satisfaction_rating || 0);
        
        // Fetch technicians data
        const techniciansData = await getTechnicians();
        setAllTechnicians(techniciansData || []);
        
        // Fetch client only if needed
        if (jobOrderData?.client_id) {
          const clientData = await getClient(jobOrderData.client_id);
          setClient(clientData);
        }

        const technician_signed = jobOrderData?.technicians?.map(({ firstname, lastname }) => `${firstname} ${lastname}`) || [];

        setjobOrderParameters((prev) => ({
          ...prev,
          status: jobOrderData?.status || "",
          job_date: jobOrderData?.job_date || "",
          start_time: jobOrderData?.start_time || "",
          end_time: getEffectiveEndTime(jobOrderData) || "",
          accomplished_by_technician: technician_signed
        }));

        setEditedClient(jobOrderData?.client);

        // Initialize findings areas and recommendations
        if (jobOrderData?.findings_areas && Array.isArray(jobOrderData.findings_areas)) {
          replaceFindingAreas(jobOrderData.findings_areas);
        }

        if (jobOrderData?.recommendations && Array.isArray(jobOrderData.recommendations)) {
          const normalizedRecommendations = jobOrderData.recommendations.map((rec) => ({
            ...rec,
            other_text: rec?.other_text ?? rec?.otherText ?? "",
          }));

          setValue("recommendations", normalizedRecommendations);
        }

        // Initialize all form data immediately after fetching
        if (jobOrderData) {
          initializeFormData(jobOrderData);
        }

      } catch (error) {
        console.error("Error fetching job order:", error);
        setErrorMessage("Error fetching job order. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchJobOrder();
  }, [id, setValue, replaceFindingAreas]);

  // Separate function to initialize form data (called once after fetch)
  const initializeFormData = (jobOrderData) => {
    // Initialize Nature of Work
    const nature = jobOrderData?.nature_of_work ?? {};
    setNatureOfWork({
      generalPestControl: Boolean(nature.general_pest_control),
      disinfection: Boolean(nature.disinfection),
      fumigation: Boolean(nature.fumigation),
      rodentControl: Boolean(nature.rodent_control),
      foggingMisting: Boolean(nature.fogging_misting),
      others: Boolean(nature.others),
    });
    setNatureOfWorkOthersText(nature.others_text ?? "");

    // Initialize Structure Type
    const st = jobOrderData?.qc_structure_type ?? {};
    setStructureType({
      residential: Boolean(st.residential),
      commercialEstablishment: Boolean(st.commercial_establishment),
      others: Boolean(st.others),
    });
    setStructureNatureOfBusiness(st.nature_of_business ?? "");
    setStructureOthersText(st.others_text ?? "");

    // Initialize GPC data
    const gpcData = jobOrderData?.gpc ?? {};
    const insectsDetail = gpcData.insects_detail ?? {};
    setGpcFlyingCrawling(Boolean(gpcData.flying_and_crawling_insects));
    setGpcInsectsDetail({
      cockroach: Boolean(insectsDetail.cockroach),
      flies: Boolean(insectsDetail.flies),
      mosquitoes: Boolean(insectsDetail.mosquitoes),
    });
    setGpcRodents(Boolean(gpcData.rodents));
    setGpcOtherPests(Boolean(gpcData.other_pests));
    setGpcOtherPestsText(gpcData.other_pests_text ?? "");

    // Initialize Action Taken
    const act = jobOrderData?.action_taken_treatment_done ?? {};
    setActionTaken({
      mistSpraying: Boolean(act.mist_spraying),
      thermalFogging: Boolean(act.thermal_fogging),
      coldFogging: Boolean(act.cold_fogging),
      larviciding: Boolean(act.larviciding),
      gelBaiting: Boolean(act.gel_baiting),
      uvLight: Boolean(act.uv_light),
      wiping: Boolean(act.wiping),
      steaming: Boolean(act.steaming),
      others: Boolean(act.others),
    });
    setActionTakenOthersText(act.others_text ?? "");

    // Initialize Areas Treated
    const areasPos = jobOrderData?.areas_treated_positions ?? [];
    const areasState = {};
    for (const { row, col } of areasPos) {
      const rIdx = (Number(row) || 1) - 1;
      const cIdx = (Number(col) || 1) - 1;
      if (
        rIdx >= 0 &&
        cIdx >= 0 &&
        AREAS_TREATED_LAYOUT[rIdx] &&
        AREAS_TREATED_LAYOUT[rIdx][cIdx]
      ) {
        areasState[makeIdxKey(rIdx, cIdx)] = true;
      }
    }
    setAreasTreatedState(areasState);
    setOtherAreasText(jobOrderData?.areas_treated_other_text ?? "");

    // Initialize Customer Responsibilities
    const cr = jobOrderData?.customers_responsibilities ?? {};
    const sanitationList = Array.isArray(cr.sanitation) ? cr.sanitation : [];
    setCustomerResp({
      sanitation1: sanitationList[0] ?? "",
      sanitation2: sanitationList[1] ?? "",
      sanitation3: sanitationList[2] ?? "",
      sealing: cr.rodent_entry_point?.sealing ?? "",
      barriers: cr.rodent_entry_point?.barriers ?? "",
      repairsMaintenance: cr.repairs_maintenance ?? "",
      renovation: cr.renovation ?? "",
    });

    // Initialize Degree of Infestation
    const degree = jobOrderData?.findings_degree_of_infestation ?? {};
    setInfestationFindings({
      Roaches: degree.roaches ?? "",
      Mosquitoes: degree.mosquitoes ?? "",
      Flies: degree.flies ?? "",
      Ants: degree.ants ?? "",
      Rodents: degree.rodents ?? "",
    });

    // Initialize Chemical Used
    const chemicalsUsed = jobOrderData?.chemical_used ?? [];
    const newChemicals = [...chemical_used];
    chemicalsUsed.forEach((chem, index) => {
      if (index < newChemicals.length) {
        newChemicals[index] = {
          label: chem.label ?? (index + 1).toString(),
          chemical: chem.chemical ?? "",
        };
      }
    });
    setChemicalUsed(newChemicals);

    // Initialize Control Against
    const ca = jobOrderData?.control_against ?? {};
    setControlAgainst({
      crawlingInsects: Boolean(ca.crawling_insects),
      flyingInsects: Boolean(ca.flying_insects),
      rodentControl: Boolean(ca.rodent_control),
      others: Boolean(ca.others),
    });
    setOtherControlText(ca.others_text ?? "");

    // Initialize other fields
    setTechnicianSignatory(jobOrderData?.technician_signatory ?? "");
    setAcknowledgedClientSignatory(jobOrderData?.acknowledgedClientSignatory ?? "");
    setAccomplishedSignatureURL(jobOrderData?.accomplishedSignature ?? "");
    setAcknowledgedSignatureURL(jobOrderData?.acknowledgedSignature ?? "");
    setSpecialNotes(jobOrderData?.special_notes ?? "");
    setAppointmentType(jobOrderData?.appointment_type ?? "");
    setTreatmentType(jobOrderData?.treatment_type ?? "");

    // Initialize document links
    setLinks({
      ...buildGeneratedDocumentLinks(jobOrderData?.id, {
        includeReport: false,
        includeCertificate: true,
        includeReceipt: true,
      }),
      ...(jobOrderData?.certificate_url && {
        certificate_url: jobOrderData.certificate_url,
      }),
      ...(jobOrderData?.receipts_url && {
        receipts_url: jobOrderData.receipts_url,
      }),
    });
  };

  // Convert booleans to "True"/"False" recursively (for PDF mapper payload)
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) return obj.map(convertBooleans);
    if (obj && typeof obj === "object") {
      return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, convertBooleans(v)]));
    }
    if (typeof obj === "boolean") return obj ? "True" : "False";
    return obj;
  };

  // Date for email body (QcForm style)
  function formatDate(dateString) {
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) throw new Error("Invalid date");
      return new Intl.DateTimeFormat("en-US", { year: "numeric", month: "long", day: "numeric" }).format(d);
    } catch {
      return "Invalid Date";
    }
  }

  const handleSendEmail = async (attachmentLinks) => {
    const recipientEmail =
      jobOrder?.client?.contact?.email?.email ||
      client?.contact?.email?.email ||
      "";

    if (!recipientEmail) {
      setErrorMessage("No client email found for this job order.");
      return;
    }

    const htmlBody = buildServiceReportEmail({
      formattedDate: formatDate(jobOrder?.job_date),
      attachmentLinks,
    });

    try {
      await sendEmailQueue({
        from: "boaz-reminders@maestrosystemsinc.com",
        recipients: [recipientEmail],
        subject: `Service Report for Job Order ${jobOrder?.id}`,
        body: htmlBody,
      });
    } catch (err) {
      console.error("Error sending email:", err);
      setErrorMessage("Failed to send email.");
      throw err;
    }
  };

  const buildClientSignatureIsoFromEditedTime = (hhmm, baseJobOrder) => {
    if (!hhmm) return baseJobOrder?.client_signature_uploaded_at || "";

    const [hour, minute] = String(hhmm).split(":").map(Number);

    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return baseJobOrder?.client_signature_uploaded_at || "";
    }

    try {
      const baseIso = baseJobOrder?.client_signature_uploaded_at;
      const base = baseIso
        ? new Date(baseIso)
        : new Date(`${baseJobOrder?.job_date}T00:00:00+08:00`);

      if (Number.isNaN(base.getTime())) {
        return baseJobOrder?.client_signature_uploaded_at || "";
      }

      const year = base.getUTCFullYear();
      const month = base.getUTCMonth();
      const date = base.getUTCDate();

      const rebuilt = new Date(Date.UTC(year, month, date, hour - 8, minute, 0));
      return rebuilt.toISOString();
    } catch {
      return baseJobOrder?.client_signature_uploaded_at || "";
    }
  };

  // Form submission - optimized
  const onSubmit = async (formData) => {
    // Early return if jobOrder is not loaded
    if (!jobOrder || !jobOrder.id) {
      setErrorMessage("Job order data is not available. Please refresh the page.");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      // Prepare areas treated positions
      const selectedPositions = [];
      Object.entries(areasTreatedState).forEach(([key, selected]) => {
        if (selected) {
          const [r, c] = key.replace('r', '').split('-c').map(Number);
          selectedPositions.push({ row: r + 1, col: c + 1 }); // 1-based for DB
        }
      });

      const updatedJobOrderParameters = {
        ...jobOrder,
        status: jobOrderParameters.status,
        job_date: jobOrderParameters.job_date,
        start_time: jobOrderParameters.start_time,
        end_time: jobOrderParameters.end_time,
        ...(usesClientSignatureAsEndTime
          ? {
              client_signature_uploaded_at: buildClientSignatureIsoFromEditedTime(
                jobOrderParameters.end_time,
                jobOrder
              ),
            }
          : {}),
        appointment_type: appointmentType,
        treatment_type: treatmentType,
        special_notes: specialNotes,
        chemical_used: chemical_used.filter(c => c.chemical.trim()),
        control_against: {
          crawling_insects: controlAgainst.crawlingInsects,
          flying_insects: controlAgainst.flyingInsects,
          rodent_control: controlAgainst.rodentControl,
          others: controlAgainst.others,
          others_text: otherControlText.trim(),
        },
        nature_of_work: {
          general_pest_control: natureOfWork.generalPestControl,
          disinfection: natureOfWork.disinfection,
          fumigation: natureOfWork.fumigation,
          rodent_control: natureOfWork.rodentControl,
          fogging_misting: natureOfWork.foggingMisting,
          others: natureOfWork.others,
          others_text: natureOfWorkOthersText.trim(),
        },
        qc_structure_type: {
          residential: structureType.residential,
          commercial_establishment: structureType.commercialEstablishment,
          nature_of_business: structureNatureOfBusiness.trim(),
          others: structureType.others,
          others_text: structureOthersText.trim(),
        },
        gpc: {
          flying_and_crawling_insects: gpcFlyingCrawling,
          insects_detail: gpcInsectsDetail,
          rodents: gpcRodents,
          other_pests: gpcOtherPests,
          other_pests_text: gpcOtherPestsText.trim(),
        },
        action_taken_treatment_done: {
          mist_spraying: actionTaken.mistSpraying,
          thermal_fogging: actionTaken.thermalFogging,
          cold_fogging: actionTaken.coldFogging,
          larviciding: actionTaken.larviciding,
          gel_baiting: actionTaken.gelBaiting,
          uv_light: actionTaken.uvLight,
          wiping: actionTaken.wiping,
          steaming: actionTaken.steaming,
          others: actionTaken.others,
          others_text: actionTakenOthersText.trim(),
        },
        areas_treated_positions: selectedPositions,
        areas_treated_other_text: otherAreasText.trim(),
        customers_responsibilities: {
          sanitation: [customerResp.sanitation1, customerResp.sanitation2, customerResp.sanitation3],
          rodent_entry_point: {
            sealing: customerResp.sealing,
            barriers: customerResp.barriers          
          },
          repairs_maintenance: customerResp.repairsMaintenance,
          renovation: customerResp.renovation,
        },
        findings_degree_of_infestation: {
          flies: infestationFindings.Flies,
          mosquitoes: infestationFindings.Mosquitoes,
          roaches: infestationFindings.Roaches,
          ants: infestationFindings.Ants,
          rodents: infestationFindings.Rodents,
        },
        findings_areas: formData.findingsAreas || [],
        recommendations: formData.recommendations || [],
        service_report_status: "Done",
        technician_signatory: technicianSignatory,
        satisfaction_rating: satisfactionRating,
        accomplishedSignature: accomplishedSignatureURL,
        acknowledgedSignature: acknowledgedSignatureURL,
        acknowledgedClientSignatory: acknowledgedClientSignatory,
      };

      // 2) Persist edits
      await putJobOrder(jobOrder.id, updatedJobOrderParameters);
      if (editedClient?.id) {
        const { id: clientId, ...putClientParameters } = editedClient;
        await putClient(clientId, putClientParameters);
      }

      // 3) Regenerate PDFs via mapper (certificate & final receipt)
      const mapperPayload = convertBooleans({
        jobOrder,
        updatedJobOrderParameters,
        form: formData,
      });
      await submitDocumentFieldMapper(mapperPayload);

      // 4) Patch or compute S3 links and update local UI state
      const newLinks = buildGeneratedDocumentLinks(jobOrder.id, {
        includeReport: false,
        includeCertificate: true,
        includeReceipt: true,
      });

      await patchJobOrderLink(jobOrder.id, newLinks);
      setLinks(newLinks);

      if (sendEmailAfterEdit) {
        await handleSendEmail(newLinks);
        setSuccessMessage("QC form updated, PDFs regenerated, and email queued!");
      } else {
        setSuccessMessage("QC form updated and PDFs regenerated successfully.");
      }
    } catch (error) {
      console.error("Error updating QC form:", error);
      setErrorMessage(`Failed to update QC Form: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const effectiveEndTime = getEffectiveEndTime(jobOrder);
  const usesClientSignatureAsEndTime = Boolean(jobOrder?.client_signature_uploaded_at);

  const pageOneJobOrder = {
    ...jobOrder,
    end_time: effectiveEndTime,
    client: jobOrder?.client || client || {},
  };

  const STEP_LABELS = {
    review_job_details: "Review Job Details Completed At",
    service_details: "Service Details Completed At",
    areas_treated: "Areas Treated Completed At",
    findings_and_recommendations: "Findings and Recommendations Completed At",
    customer_responsibilities: "Customer Responsibilities Completed At",
    chemical_and_control: "Chemical Used / Control Against Completed At",
    technician_confirmation: "For Technicians Only Completed At",
    client_signature: "For Client Only - Signature Completed At",
    client_satisfaction: "For Client Only - Satisfaction Completed At",
  };

  const getLatestStepLogMap = (logs = []) => {
    return (Array.isArray(logs) ? logs : []).reduce((acc, log) => {
      const stepKey = log?.step_key;
      const timestamp = log?.timestamp;

      if (!stepKey || !timestamp) return acc;

      const prev = acc[stepKey];
      if (!prev || new Date(timestamp) > new Date(prev)) {
        acc[stepKey] = timestamp;
      }

      return acc;
    }, {});
  };

  const latestStepLogMap = getLatestStepLogMap(jobOrder?.report_page_logs);

  const getStepTimestamp = (stepKey) =>
    jobOrder?.[`${stepKey}_completed_at`] || latestStepLogMap?.[stepKey] || "";

  const PEST_TYPES = ['Flies', 'Mosquitoes', 'Roaches', 'Ants', 'Rodents'];
  const LEVELS = ["L", "M", "H"];
  const MAX_FINDINGS_AREAS = 6;
  
  const handleCustomerRespValueChange = (field, value) => {
    setCustomerResp((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const technicianSectionJobOrder = {
    ...jobOrder,
    job_date: jobOrderParameters?.job_date || jobOrder?.job_date || "",
    start_time: jobOrderParameters?.start_time || jobOrder?.start_time || "",
    end_time: jobOrderParameters?.end_time || effectiveEndTime || "",
    accomplished_by_technician:
      jobOrderParameters?.accomplished_by_technician ||
      jobOrder?.accomplished_by_technician ||
      jobOrder?.technicians ||
      [],
    technician_signatory: technicianSignatory || jobOrder?.technician_signatory || "",
    accomplishedSignature: accomplishedSignatureURL || jobOrder?.accomplishedSignature || "",
  };

  const clientSectionJobOrder = {
    ...jobOrder,
    acknowledgedClientSignatory:
      acknowledgedClientSignatory || jobOrder?.acknowledgedClientSignatory || "",
    acknowledgedSignature:
      acknowledgedSignatureURL || jobOrder?.acknowledgedSignature || "",
    satisfaction_rating:
      satisfactionRating || jobOrder?.satisfaction_rating || 0,
    client_satisfaction_remarks:
      jobOrder?.client_satisfaction_remarks || "",
  };

  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon sx={{ fontSize: "40px", marginRight: "8px" }} />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Edit Service Report"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form onSubmit={handleFormSubmit(onSubmit)} className="job-order-form">
        <Grid container>
          <PageOneReviewSection
            jobOrder={pageOneJobOrder}
            client={client}
            selectedJobTypes={displayedJobTypes}
            selectedJobDescriptions={displayedJobDescriptions}
            jobOrderParameters={{ status: jobOrder?.status || "" }}
            errors={{}}
            onStatusChange={(value) =>
              setJobOrder((prev) => ({
                ...prev,
                status: value,
              }))
            }
            showStatus={true}
            readOnly={false}
          />

          {!isTechnician && (
            <Grid item xs={12}>
              <SectionTimestampNotice
                label={STEP_LABELS.review_job_details}
                value={getStepTimestamp("review_job_details")}
                colorSet="info"
              />
            </Grid>
          )}

        {/* Service Details Section */}
        <QcServiceDetailsSection
          mode="edit"
          structureType={structureType}
          onStructureTypeChange={handleStructureTypeChange}
          structureNatureOfBusiness={structureNatureOfBusiness}
          onStructureNatureOfBusinessChange={setStructureNatureOfBusiness}
          structureOthersText={structureOthersText}
          onStructureOthersTextChange={setStructureOthersText}

          natureOfWork={natureOfWork}
          onNatureOfWorkChange={handleNatureOfWorkChange}
          natureOfWorkOthersText={natureOfWorkOthersText}
          onNatureOfWorkOthersTextChange={setNatureOfWorkOthersText}

          gpcMain={{
            insects: gpcFlyingCrawling,
            rodents: gpcRodents,
            other: gpcOtherPests,
          }}
          gpcInsects={gpcInsectsDetail}
          gpcOtherText={gpcOtherPestsText}
          onGpcMainChange={(field) => (e) => {
            const checked = e.target.checked;

            if (field === "insects") setGpcFlyingCrawling(checked);
            if (field === "rodents") setGpcRodents(checked);
            if (field === "other") setGpcOtherPests(checked);
          }}
          onGpcInsectChange={(field) => (e) => {
            const checked = e.target.checked;
            setGpcInsectsDetail((prev) => ({
              ...prev,
              [field]: checked,
            }));
          }}
          onGpcOtherTextChange={setGpcOtherPestsText}

          actionTaken={actionTaken}
          onActionTakenChange={handleActionTakenChange}
          actionTakenOthersText={actionTakenOthersText}
          onActionTakenOthersTextChange={setActionTakenOthersText}

          errors={errors}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.service_details}
              value={getStepTimestamp("service_details")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* AREAS TREATED Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  AREAS TREATED
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <AreasTable
                  layout={AREAS_TREATED_LAYOUT}
                  state={areasTreatedState}
                  onToggle={toggleAreaTreated}
                  ariaLabel="areas_treated"
                  getKey={(rIdx, cIdx /*, label */) => makeIdxKey(rIdx, cIdx)}
                />

                <TextField
                  label="Please specify (Other areas)"
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  value={otherAreasText}
                  onChange={(e) => setOtherAreasText(e.target.value)}
                  disabled={!OTHER_AREAS_KEY || !areasTreatedState[OTHER_AREAS_KEY]}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.areas_treated}
              value={getStepTimestamp("areas_treated")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* FINDINGS and RECOMMENDATIONS Section */}
        <QcFindingsRecommendationsSection
          mode="edit"
          control={control}
          register={register}
          setValue={setValue}
          clearErrors={() => {}}
          errors={{}}
          pestTypes={PEST_TYPES}
          levels={LEVELS}
          infestationFindings={infestationFindings}
          onInfestationChange={(pest, value) => handleInfestationChange(pest, value)}
          ratingBtnSx={ratingBtnSx}
          findingsAreas={findingsAreas}
          appendFindingArea={() =>
            addFinding({
              location: "",
              pests: {
                Roaches: 0,
                Mosq: 0,
                Rodents: 0,
                Flies: 0,
                Ants: 0,
                Others: 0,
              },
            })
          }
          removeFindingArea={removeFinding}
          maxFindingsAreas={MAX_FINDINGS_AREAS}
          recFields={recFields}
          recValues={recValues}
          appendRec={() => appendRec({ option: "", other_text: "" })}
          removeRec={removeRec}
          recommendationOptions={RECOMMENDATION_OPTIONS}
          maxRecommendations={MAX_RECOMMENDATIONS}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.findings_and_recommendations}
              value={getStepTimestamp("findings_and_recommendations")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* CUSTOMER'S RESPONSIBILITIES Section */}
        <QcCustomerResponsibilitiesSection
          mode="edit"
          value={customerResp}
          onChange={handleCustomerRespValueChange}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.customer_responsibilities}
              value={getStepTimestamp("customer_responsibilities")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Chemical Used Section */}
        <QcChemicalControlSection
          mode="edit"
          chemicalUsed={chemical_used}
          onChemicalChange={handleChemicalInputChange}
          controlAgainst={controlAgainst}
          onControlAgainstChange={handleControlAgainstChange}
          otherControlText={otherControlText}
          onOtherControlTextChange={setOtherControlText}
          errors={errors}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.chemical_and_control}
              value={getStepTimestamp("chemical_and_control")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Technician Only Section */}
        <TechnicianOnlyReviewSection
          jobOrder={technicianSectionJobOrder}
          isTechnician={isTechnician}
          editableSchedule={true}
          onStartTimeChange={(value) => handleJObOrderChange("start_time", value)}
          onEndTimeChange={(value) => {
            handleJObOrderChange("end_time", value);

            if (usesClientSignatureAsEndTime) {
              setJobOrder((prev) => ({
                ...prev,
                client_signature_uploaded_at: buildClientSignatureIsoFromEditedTime(
                  value,
                  prev
                ),
              }));
            }
          }}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.technician_confirmation}
              value={getStepTimestamp("technician_confirmation")}
              colorSet="info"
            />
          </Grid>
        )}
      
        {/* Customer Only Section */}
        <ClientOnlyReviewSection
          jobOrder={clientSectionJobOrder}
          isTechnician={isTechnician}
        />

        {/* Document Links Section */}
        <DocumentLinksCard links={links} />

        {/* ⏰ TIMESTAMP DISPLAY - Report Submission (HIDDEN FROM TECHNICIANS) */}
        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label="Report Submitted At"
              value={jobOrder?.report_submitted_at}
              colorSet="warning"
            />
          </Grid>
        )}

        {/* Action Buttons Section */}
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mt={2}
          mb={2}
          gap={2}
        >
          <FormControlLabel
            sx={{
              mr: 2,
              ".MuiFormControlLabel-label": {
                fontSize: "0.875rem",
              },
            }}
            control={
              <Checkbox
                checked={sendEmailAfterEdit}
                onChange={(e) => setSendEmailAfterEdit(e.target.checked)}
                disabled={isLoading}
              />
            }
            label="Send email after save"
          />

          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              if (returnTo) {
                window.location.href = returnTo;
              } else {
                navigate("/manage-service-reports");
              }
            }}
            sx={{
              fontSize: "0.875rem",
              color: "#FF4C4C",
              borderColor: "#FF4C4C",
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading}
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            {isLoading
              ? "Saving..."
              : sendEmailAfterEdit
              ? "Save & Send Email"
              : "Save Changes"}
          </Button>
        </Grid>
      </Grid>
      </form>
    </BaseFormWrapper>
  );
}

export default EditQcForm;
